import { useState, useEffect } from "react";
import alternative from "assets/images/logo.jpg";
import GeneralInfo from "./GeneralInfo";
import Sections from "./Sections";
import Payment from "./Payment";

// ── Countdown ─────────────────────────────────────────────────────────────────
const OPEN_DATE = new Date("2026-02-26T20:26:00");
const pad = (n) => String(n).padStart(2, "0");

const calculateTimeLeft = () => {
  const diff = OPEN_DATE - new Date();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

function CountdownView() {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      const tl = calculateTimeLeft();
      setTimeLeft(tl);
      if (!tl) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const units = [
    { value: timeLeft?.days ?? 0, label: "Өдөр" },
    { value: timeLeft?.hours ?? 0, label: "Цаг" },
    { value: timeLeft?.minutes ?? 0, label: "Минут" },
    { value: timeLeft?.seconds ?? 0, label: "Секунд" },
  ];

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-[#f3eeff] via-white to-[#ede9fe] font-raleway overflow-hidden px-4">
      <div className="absolute top-[-80px] left-[-80px] w-72 h-72 bg-purple-200 rounded-full opacity-30 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-60px] right-[-60px] w-64 h-64 bg-indigo-200 rounded-full opacity-30 blur-3xl pointer-events-none" />
      <img src={alternative} alt="Томүжин" className="w-36 sm:w-48 md:w-64 object-contain mb-6 drop-shadow-sm" />
      <h1 className="text-xl sm:text-2xl md:text-4xl font-bold text-[#5c1090] text-center leading-tight mb-2">
        Өргөдөл нээгдэх хүртэл
      </h1>
      <p className="text-gray-500 text-xs sm:text-sm text-center mb-8 max-w-sm">
        {`${OPEN_DATE.toLocaleDateString("mn-MN")} — ${OPEN_DATE.toLocaleTimeString("mn-MN", { hour: "2-digit", minute: "2-digit", hour12: false })}`}
      </p>
      <div className="grid grid-cols-4 gap-2 sm:gap-4 w-full max-w-xs sm:max-w-sm md:max-w-md mb-10">
        {units.map(({ value, label }, i) => (
          <div key={i} className="flex flex-col items-center">
            <div className="relative flex items-center justify-center w-full rounded-xl bg-white shadow-md border border-purple-100 overflow-hidden" style={{ aspectRatio: "1" }}>
              <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/60 to-transparent pointer-events-none" />
              <span className="text-lg sm:text-2xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-[#7c3aed] to-[#5c1090] font-raleway z-10">
                {pad(value)}
              </span>
            </div>
            <span className="mt-1 text-[9px] sm:text-xs font-semibold text-gray-500 uppercase tracking-widest text-center">
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Steps indicator ───────────────────────────────────────────────────────────
const STEPS = ["Ерөнхий мэдээлэл", "Асуултууд", "Төлбөр"];

function StepsBar({ current }) {
  return (
    <div className="flex items-center justify-center gap-2 py-3 px-4 bg-white border-b border-gray-100 shadow-sm sticky top-0 z-50">
      {STEPS.map((label, i) => {
        const isDone = i < current;
        const isActive = i === current;
        return (
          <div key={i} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all
              ${isDone ? "bg-green-100 text-green-700" :
                isActive ? "bg-[#7B2FBE] text-white" :
                  "bg-gray-100 text-gray-400"}`}>
              <span>{isDone ? "✓" : i + 1}</span>
              <span className="hidden sm:inline">{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`w-6 h-0.5 ${i < current ? "bg-green-300" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ApplicationPage() {
  const [opened, setOpened] = useState(!calculateTimeLeft());
  const [step, setStep] = useState(0);
  const [generalData, setGeneralData] = useState({});
  const [appData, setAppData] = useState(null);
  const [stageId, setStageId] = useState(null);
  const [showFilledModal, setShowFilledModal] = useState(false);

  useEffect(() => {
    if (opened) return;
    const timer = setInterval(() => {
      if (!calculateTimeLeft()) { setOpened(true); clearInterval(timer); }
    }, 1000);
    return () => clearInterval(timer);
  }, [opened]);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const handleGeneralNext = (data, registerResponse) => {
    setGeneralData(data);
    setAppData(registerResponse);
    setStageId(registerResponse.stageId);
    if (registerResponse.processStatus === "filled") {
      setShowFilledModal(true);
    } else {
      setStep(1);
      scrollTop();
    }
  };

  const handleSectionsNext = () => {
    setStep(2);
    scrollTop();
  };

  const handleSuccess = () => {
    setStep(3);
    scrollTop();
  };

  if (!opened) return <CountdownView />;
  if (step === 3) return <SuccessView />;

  return (
    <div className="min-h-screen bg-[#f7f4fc] font-raleway">
      <StepsBar current={step} />
      <div className="flex justify-center pt-6 pb-2">
        <img src={alternative} alt="Томүжин" className="w-24 sm:w-32 object-contain drop-shadow-sm" />
      </div>

      {/* ── FILLED MODAL ── */}
      {showFilledModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999, padding: 16,
        }}>
          <div style={{
            background: "#fff", borderRadius: 20,
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
            padding: "32px 28px", maxWidth: 420, width: "100%",
            textAlign: "center", fontFamily: "inherit",
          }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>📋</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: "#1a1a2e", margin: "0 0 10px" }}>
              Өргөдөл бөглөгдсөн байна
            </h3>
            <p style={{ fontSize: 14, color: "#666", lineHeight: 1.7, margin: "0 0 24px" }}>
              Та өмнө нь өргөдлөө бөглөж дуусгасан байна.
              Төлбөр төлөх хэсэг рүү шилжих үү?
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => {
                  setShowFilledModal(false);
                  setStep(1);
                  scrollTop();
                }}
                style={{
                  background: "#eee", color: "#444", border: "none",
                  borderRadius: 50, padding: "12px 24px",
                  fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                ✏️ Өргөдөл засах
              </button>
              <button
                onClick={() => {
                  setShowFilledModal(false);
                  setStep(2);
                  scrollTop();
                }}
                style={{
                  background: "#7B2FBE", color: "#fff", border: "none",
                  borderRadius: 50, padding: "12px 28px",
                  fontSize: 14, fontWeight: 800, cursor: "pointer",
                  fontFamily: "inherit",
                  boxShadow: "0 4px 16px rgba(123,47,190,0.3)",
                }}
              >
                💳 Төлбөр төлөх →
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 0 && (
        <GeneralInfo
          onNext={handleGeneralNext}
          initialData={Object.keys(generalData).length ? generalData : null}
        />
      )}
      {step === 1 && (
        <Sections
          stageId={stageId}
          applicantAdmissionId={appData?.applicantAdmissionId}
          onNext={handleSectionsNext}
          onBack={() => { setStep(0); scrollTop(); }}
        />
      )}
      {step === 2 && (
        <Payment
          applicantAdmissionId={appData?.applicantAdmissionId}
          onSuccess={handleSuccess}
          onBack={() => { setStep(1); scrollTop(); }}
        />
      )}
    </div>
  );
}

// ── Success ───────────────────────────────────────────────────────────────────
function SuccessView() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center font-raleway bg-[#f7f4fc]">
      <div className="text-6xl mb-4">🎉</div>
      <h2 className="text-2xl font-extrabold text-[#4a0080] mb-3">Өргөдөл амжилттай илгээгдлээ!</h2>
      <p className="text-gray-500 text-sm max-w-sm leading-relaxed mb-6">
        Таны өргөдлийг хүлээн авлаа. Бид 2-3 ажлын өдрийн дотор
        имэйлээр хариу илгээх болно.
      </p>
      <div className="bg-purple-50 rounded-2xl p-5 max-w-sm w-full text-left mb-6">
        <div className="text-xs font-bold text-[#7B2FBE] uppercase tracking-widest mb-2">📋 Дараагийн алхам</div>
        <div className="text-sm text-gray-600 leading-relaxed">
          Портал руу нэвтрэхдээ:<br />
          <span className="text-gray-400 text-xs">Имэйл + утасны дугаар (нууц үг)</span>
        </div>
      </div>
      <a href="https://portal.tomujin.edu.mn"
        className="bg-[#FFD600] text-gray-900 font-extrabold px-8 py-3 rounded-full text-sm shadow-md hover:scale-105 transition-transform">
        Портал руу нэвтрэх →
      </a>
    </div>
  );
}